import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BellRing, CalendarClock, FileText, IdCard, Megaphone, Plus, Send, SlidersHorizontal } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchBroadcasts, createBroadcast, type AdminBroadcast } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'

const AUDIENCES = ['all', 'active', 'staff'] as const

/**
 * A15.01/02 — Broadcast/e'lon composer + tarixi. GET/POST /admin/broadcasts.
 * Compose faqat DRAFT yozadi (backend hech kimga yubormaydi) — tasodifiy spam
 * yo'q; haqiqiy yuborish alohida gated qadam (follow-up).
 */
export function BroadcastScreen() {
  const t = useT()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [showForm, setShowForm] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [form, setForm] = useState({ title: '', body: '', audience: 'all', schedule: '' })
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (form.body.trim().length < 1) return
    setBusy(true)
    try {
      const schedMs = form.schedule ? new Date(form.schedule).getTime() : 0
      await createBroadcast({
        ...(form.title.trim() ? { title: form.title.trim() } : {}),
        body: form.body.trim(),
        audience: form.audience,
        ...(schedMs > 0 ? { scheduled_at: schedMs } : {}),
      })
      toast(t.broadcast.created, { tone: 'success' })
      setForm({ title: '', body: '', audience: 'all', schedule: '' })
      setShowForm(false)
      setReloadKey((n) => n + 1)
    } catch (e) {
      toast(t.broadcast.createFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setBusy(false)
    }
  }

  const rowActions = useMemo<RowAction<AdminBroadcast>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/broadcasts/${r.id}`),
      },
    ],
    [t, navigate],
  )

  const columns = useMemo<AdminColumn<AdminBroadcast>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
      { key: 'title', header: t.broadcast.titleField, type: 'text', minWidth: 160 },
      { key: 'body', header: t.broadcast.bodyField, type: 'longtext', minWidth: 240 },
      {
        key: 'audience',
        header: t.broadcast.audience,
        type: 'enum',
        width: 120,
        options: AUDIENCES.map((a) => ({ value: a, label: t.broadcast[a], status: 'review' })),
      },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'draft', label: t.broadcast.draft, status: 'pending' },
          { value: 'queued', label: t.broadcast.queued, status: 'review' },
          { value: 'sent', label: t.broadcast.sent, status: 'active' },
          { value: 'cancelled', label: t.broadcast.cancelled, status: 'deleted' },
          { value: 'failed', label: t.broadcast.failed, status: 'deleted' },
        ],
      },
      { key: 'recipients', header: t.broadcast.recipients, type: 'text', width: 110 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchBroadcasts, [])

  return (
    <>
      <PageHeader
        title={t.broadcast.title}
        screenId="A15.01"
        description={t.broadcast.note}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/broadcasts/scheduled"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <CalendarClock size={13} />
              {t.broadcast.scheduledTitle}
            </Link>
            <Link
              to="/broadcasts/templates"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <FileText size={13} />
              {t.broadcast.templates}
            </Link>
            <Link
              to="/push/health"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <BellRing size={13} />
              {t.broadcast.pushHealth}
            </Link>
            <Link
              to="/broadcasts/settings"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <SlidersHorizontal size={13} />
              {t.broadcast.policy}
            </Link>
            <Link
              to="/broadcasts/banner"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Megaphone size={13} />
              {t.broadcast.banner}
            </Link>
            <button
              onClick={() => setShowForm((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-accent bg-accent-subtle px-2.5 py-1 text-sm text-accent hover:bg-accent hover:text-white"
            >
              <Plus size={13} />
              {t.broadcast.compose}
            </button>
          </div>
        }
      />
      {showForm && (
        <div className="flex flex-col gap-2 border-b border-border-subtle bg-surface-2 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={t.broadcast.titleField}
              className="min-w-40 flex-1 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
            />
            <select
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
              className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
            >
              {AUDIENCES.map((a) => (
                <option key={a} value={a}>
                  {t.broadcast[a]}
                </option>
              ))}
            </select>
            <input
              type="datetime-local"
              title={t.broadcast.schedule}
              value={form.schedule}
              onChange={(e) => setForm({ ...form, schedule: e.target.value })}
              className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
            />
          </div>
          <textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder={t.broadcast.bodyField}
            rows={3}
            className="rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          />
          {/* A15.06 — live preview of the composed message. */}
          {form.body.trim() && (
            <div className="rounded-lg border border-border-default bg-surface-1 p-3">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-tertiary">
                {t.broadcast.preview}
              </div>
              {form.title.trim() && <div className="text-sm font-semibold text-primary">{form.title}</div>}
              <div className="whitespace-pre-wrap text-sm text-secondary">{form.body}</div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs text-tertiary">{t.broadcast.draftNote}</span>
            <button
              disabled={busy || form.body.trim().length < 1}
              onClick={submit}
              className="inline-flex items-center gap-1.5 rounded-md border border-accent bg-accent-subtle px-3 py-1.5 text-sm text-accent hover:bg-accent hover:text-white disabled:opacity-50"
            >
              <Send size={13} />
              {t.broadcast.saveDraft}
            </button>
          </div>
        </div>
      )}
      <DataTable<AdminBroadcast>
        key={reloadKey}
        screenId="A15.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Megaphone size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
